/* Thin client for the Factumis app backend (app/server), which proxies
   B2Brouter. Same-origin in production (the backend serves this SPA build);
   in dev, Vite proxies /api -> http://localhost:3002 (see vite.config.ts). */

const BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// Routes publiques (hors session). On NE redirige PAS un 401 reçu depuis ces
// pages — sinon boucle de redirection (la page de login appelle des endpoints).
const AUTH_PATHS = ['/connexion', '/inscription', '/oubli-mot-de-passe', '/reinitialiser', '/verifier-email', '/invitation', '/onboarding'];
let redirecting = false;

/**
 * Session expirée en cours d'usage → un 401 sur un endpoint /api. On renvoie vers
 * /connexion en mémorisant la destination (?next=) pour y revenir après login,
 * plutôt que de laisser l'écran tomber en « Impossible de charger les données ».
 * Garde anti-boucle : une seule redirection, et jamais depuis une page publique.
 */
function handleUnauthorized(): void {
  if (typeof window === 'undefined' || redirecting) return;
  const { pathname, search } = window.location;
  if (AUTH_PATHS.some((p) => pathname.startsWith(p))) return;
  redirecting = true;
  window.location.assign(`/connexion?next=${encodeURIComponent(pathname + search)}`);
}

/** Traduction des codes d'erreur de l'API en messages affichables.
    Beaucoup de routes répondent `{ error: 'CODE' }` sans `message` : sans cette
    table, les écrans affichaient le code brut (« INVALID_INPUT ») à l'utilisateur.
    Un `message` fourni par le serveur reste toujours prioritaire : il est plus
    précis que le libellé générique du code. */
const CODE_MESSAGES: Record<string, string> = {
  INVALID_INPUT: 'Certaines informations saisies sont invalides. Vérifiez le formulaire.',
  NOT_FOUND: 'Élément introuvable. Il a peut-être été supprimé entre-temps.',
  FORBIDDEN: "Vous n'avez pas les droits nécessaires pour cette action.",
  CONFLICT: "Cette action entre en conflit avec l'état actuel. Rechargez la page.",
  INTERNAL: 'Une erreur interne est survenue. Réessayez dans un instant.',
  UPSTREAM: 'Le service de facturation électronique est momentanément indisponible. Réessayez.',
  SEARCH_UPSTREAM: "L'annuaire des entreprises est momentanément indisponible. Réessayez.",
  BODY_TOO_LARGE: 'Le contenu envoyé est trop volumineux.',
  QUOTA_REACHED: "Vous avez atteint la limite de votre offre. Changez d'offre pour continuer.",
  // Raccordement à la Plateforme Agréée
  EINVOICE_NOT_READY: "Votre raccordement à la plateforme de facturation n'est pas encore actif.",
  PA_NOT_ACTIVE: "Votre raccordement à la plateforme de facturation n'est pas actif.",
  PA_SUSPENDED: 'Votre accès à la plateforme de facturation est suspendu.',
  WEBHOOKS_INACTIVE: 'Les notifications de la plateforme ne sont pas actives.',
  SELLER_VAT_MISSING: 'Votre numéro de TVA intracommunautaire est manquant : renseignez votre SIREN dans les Paramètres.',
  SIREN_REQUIRED: 'Un SIREN valide est requis : renseignez-le dans les Paramètres.',
  // Cycle de vie des factures et devis
  ALREADY_SENT: 'Cette facture a déjà été envoyée.',
  ALREADY_CASHED: 'Cette facture est déjà marquée comme encaissée.',
  ALREADY_CONVERTED: 'Ce devis a déjà été converti en facture.',
  ALREADY_SUBMITTED: 'Cette déclaration a déjà été transmise.',
  NOT_DRAFT: "Cette action n'est possible que sur un brouillon.",
  NOT_A_DRAFT: "Cette action n'est possible que sur un brouillon.",
  DRAFT_NOT_DOWNLOADABLE: "Un brouillon n'a pas encore de document téléchargeable.",
  NOT_CONVERTIBLE: 'Ce devis ne peut pas être converti en facture.',
  NOT_CASHABLE: 'Cette facture ne peut pas être encaissée.',
  INVALID_PAYMENT_DATE: "La date d'encaissement est invalide.",
  STATE_REFUSED: 'La facture a été refusée : cette action est impossible.',
  SEND_DISABLED: "L'envoi est désactivé sur cette organisation.",
  NO_EMAIL: 'Aucune adresse e-mail renseignée.',
  NO_CLIENT_EMAIL: "Ce client n'a pas d'adresse e-mail : renseignez-la sur sa fiche.",
  CLIENT_INVALID: 'Les informations du client sont incomplètes ou invalides.',
  EMAIL_FAILED: "L'e-mail n'a pas pu être envoyé. Réessayez.",
  MICRO_DISABLED: 'Le régime micro-entrepreneur est désactivé dans vos Paramètres.',
};

/** Repli par code HTTP quand le corps ne porte ni message ni code connu : toujours
    préférable au libellé technique (« PUT /api/... -> 400 »). */
const STATUS_MESSAGES: Record<number, string> = {
  400: 'Requête invalide. Vérifiez les informations saisies.',
  401: 'Votre session a expiré. Reconnectez-vous.',
  403: "Vous n'avez pas les droits nécessaires pour cette action.",
  404: 'Élément introuvable.',
  409: "Cette action entre en conflit avec l'état actuel. Rechargez la page.",
  413: 'Le contenu envoyé est trop volumineux.',
  422: 'Les informations envoyées ont été refusées.',
  429: 'Trop de requêtes. Patientez un instant avant de réessayer.',
  500: 'Une erreur interne est survenue. Réessayez dans un instant.',
  502: 'Le service est momentanément indisponible. Réessayez.',
  503: 'Le service est momentanément indisponible. Réessayez.',
  504: 'Le service met trop de temps à répondre. Réessayez.',
};

/** Lit le message d'erreur du corps JSON (best-effort), sinon le libellé français du
    code, sinon celui du statut HTTP, sinon un libellé technique.
    `code` = identifiant machine (`code` sinon `error` du corps) : les écrans testent
    ce code, jamais le texte français (qui peut changer librement). */
async function errorFrom(res: Response, fallback: string): Promise<ApiError> {
  if (res.status === 401) handleUnauthorized();
  const json = (await res.json().catch(() => ({}))) as { message?: string; error?: string; code?: string };
  const code = json.code ?? json.error;
  const message =
    json.message ||
    (code && Object.hasOwn(CODE_MESSAGES, code) ? CODE_MESSAGES[code] : undefined) ||
    STATUS_MESSAGES[res.status] ||
    fallback;
  return new ApiError(message, res.status, code);
}

/** Message affichable pour une erreur remontée par un appel API.
    Seule une `ApiError` porte un message métier : il vient du serveur, sinon de
    CODE_MESSAGES, sinon de STATUS_MESSAGES. Toute autre erreur est technique
    (« Failed to fetch » d'un réseau coupé, une exception de parsing…) et n'a rien
    à faire sous les yeux de l'utilisateur, a fortiori au moment d'émettre une
    facture. À utiliser partout plutôt que `err instanceof Error ? err.message`. */
export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.message) return err.message;
  // L'utilisateur voit le repli métier, mais la cause technique reste tracée en
  // console : sans elle, un support sur « ça n'a pas marché » n'a plus rien à lire.
  console.warn('[factumis] erreur non-ApiError, repli affiché :', fallback, err);
  return fallback;
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { headers: { Accept: 'application/json' }, signal });
  if (!res.ok) throw await errorFrom(res, `GET ${path} -> ${res.status}`);
  return (await res.json()) as T;
}

export async function apiPost<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw await errorFrom(res, `POST ${path} -> ${res.status}`);
  return (await res.json().catch(() => ({}))) as T;
}

export async function apiPut<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw await errorFrom(res, `PUT ${path} -> ${res.status}`);
  return (await res.json().catch(() => ({}))) as T;
}

export async function apiPatch<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw await errorFrom(res, `PATCH ${path} -> ${res.status}`);
  return (await res.json().catch(() => ({}))) as T;
}

export async function apiDelete<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!res.ok) throw await errorFrom(res, `DELETE ${path} -> ${res.status}`);
  return (await res.json().catch(() => ({}))) as T;
}
